import {Button} from '@wandb/weave/components/Button';
import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useMemoizedSelector} from '../../../../state/hooks';
import {useSingleRunModeContext} from '../../../MultiRunWorkspace/SingleRunMode';
import {type MediaConfigWithDefaults} from '../../../PanelMediaBrowser/Component';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useSectionSettingsContextMaybe} from '../SectionSettingsContextProvider';
import {
  useMediaSettingsActions,
  useMediaSettingsContext,
} from './contexts/MediaSettingsContextProvider';
import {MediaSettingsDrawerSectionOverrides} from './MediaSettingsDrawerSectionOverrides';
import {Subsettings} from './MediaSettingsDrawerSectionSubsettings';
import {makeAnyMediaSettingVisibleSelector} from './mediaSettingsSelectors';
import {
  type ContextProviderState,
  type MediaSetting,
  type MediaSettingsSection,
} from './types';

const EMPTY_COMPONENT_KEY_SELECTOR = () => () => '';
const EMPTY_SHOW_OVERRIDES_SELECTOR = () => () => false;
const ALWAYS_SHOW_SELECTOR = () => () => true;

type MediaSettingRowProps = {
  setting: MediaSetting;
  config: MediaConfigWithDefaults;
  numberOfOverridesBelow: ContextProviderState['numberOfOverridesBelow'];
  clearOverridesBelow: ContextProviderState['clearOverridesBelow'];
};

const MediaSettingRow = ({
  setting,
  config,
  numberOfOverridesBelow,
  clearOverridesBelow,
}: MediaSettingRowProps) => {
  const {
    component: SettingComponent,
    getComponentKey,
    getComponentKeySelector,
    showOverridesSelector,
    shouldShowSelector,
    configKeys,
    subsettings,
  } = setting;
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {settingsLevel, panelRef} = useMediaSettingsActions();
  const {isSingleRun} = useSingleRunModeContext();
  const selectorComponentKey = useMemoizedSelector(
    getComponentKeySelector ?? EMPTY_COMPONENT_KEY_SELECTOR,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef,
    isSingleRun
  );

  const selectorShowOverrides = useMemoizedSelector(
    showOverridesSelector ?? EMPTY_SHOW_OVERRIDES_SELECTOR,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );

  const shouldShow = useMemoizedSelector(
    shouldShowSelector ?? ALWAYS_SHOW_SELECTOR,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );

  // Temporarily fall back until we migrate all settings to use a selector
  const componentKey = getComponentKeySelector
    ? selectorComponentKey
    : (getComponentKey?.(config) ?? '');

  const showOverrides = showOverridesSelector
    ? selectorShowOverrides
    : configKeys.some(configKey => config[configKey] != null);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="flex flex-col gap-24 pt-16 first:pt-0">
      <div key={componentKey}>
        <SettingComponent />
      </div>
      {subsettings && <Subsettings subsettings={subsettings} />}
      {showOverrides && (
        <MediaSettingsDrawerSectionOverrides
          configKeys={configKeys}
          numberOfOverridesBelow={numberOfOverridesBelow}
          clearOverridesBelow={clearOverridesBelow}
        />
      )}
    </div>
  );
};

export const MediaSettingsDrawerSection = ({
  title,
  settings,
}: MediaSettingsSection) => {
  const [isOpen, setIsOpen] = useState(true);
  const {config, numberOfOverridesBelow, clearOverridesBelow} =
    useMediaSettingsContext();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {settingsLevel, panelRef} = useMediaSettingsActions();
  const anySettingVisible = useMemoizedSelector(
    makeAnyMediaSettingVisibleSelector,
    settings,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );

  if (!anySettingVisible) {
    return null;
  }

  return (
    <>
      <div className="mt-0 flex flex-col first:mt-4">
        <div
          className="my-12 flex h-32 cursor-pointer items-center justify-between text-wb-text-tertiary hover:text-wb-text-secondary dark:hover:text-moon-350"
          onClick={() => setIsOpen(!isOpen)}>
          <div className="text-[15px] font-semibold uppercase tracking-wide">
            {title}
          </div>
          <Button
            data-test={`media-settings-drawer-section-toggle-${title}`}
            icon={isOpen ? 'chevron-up' : 'chevron-down'}
            variant="ghost"
            size="small"
          />
        </div>
        {isOpen && (
          <div data-test={`media-settings-drawer-section-${title}`}>
            {settings.map((setting, index) => (
              <MediaSettingRow
                // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                key={`${title}-${index}`}
                setting={setting}
                config={config}
                numberOfOverridesBelow={numberOfOverridesBelow}
                clearOverridesBelow={clearOverridesBelow}
              />
            ))}
          </div>
        )}
      </div>
      <div
        role="separator"
        className={twMerge('h-px bg-wb-border', isOpen && 'mt-16')}
      />
    </>
  );
};
